import { catColors } from "@/domain/categories";
import type { ResolvedLayer } from "@/domain/profile-resolver/index";
import type { Binding, CatColors, Category, Layers } from "@/domain/types";
import { ZONE_LEGENDS } from "@/domain/zones";
import { MOUSE_ZONES, type ZoneSpec } from "./geometry";

/** A zone ready to render: design geometry plus resolved binding, colors, provenance. */
export interface RenderZone extends ZoneSpec {
  /** The fixed zone legend: supplied here, never by a Keyboard Layout. */
  legend: string;
  binding: Binding | null;
  colors: CatColors | null;
  /** The combo that defines this binding ("" base); provenance. */
  sourceCombo?: string;
}

/**
 * The mouse map's compose step: a resolved Layer into renderable zones. The mouse
 * map is composed from the Profile alone - no Keyboard, no Keyboard
 * Layout - so this is the board compose minus both axes. All seven zones always
 * return; an unbound zone renders as an unbound shell.
 */
export function composeMouse(
  layer: ResolvedLayer,
  categories: Record<string, Category>,
): RenderZone[] {
  return MOUSE_ZONES.map((z) => {
    const resolved = layer[z.code] ?? null;
    const binding = resolved?.binding ?? null;
    const zone: RenderZone = {
      ...z,
      legend: ZONE_LEGENDS[z.code],
      binding,
      colors: binding ? catColors(categories[binding.category]) : null,
    };
    if (resolved) zone.sourceCombo = resolved.combo;
    return zone;
  });
}

const LEFT_COLUMN = ["Mouse4", "Mouse5"];
const RIGHT_COLUMN = ["WheelUp", "MouseMiddle", "WheelDown"];

export interface ChipReservation {
  left: boolean;
  right: boolean;
}

/**
 * Whether each annotation chip column reserves space: decided
 * by which zone codes the Profile can ever place, never by the resolved face, so
 * hovering, switching modifier layers, or changing the command-card selection
 * never reflows the canvas. Slice 20 (compare) widens this check to both boards.
 */
export function columnReservation(codes: ReadonlySet<string>): ChipReservation {
  const bound = (column: string[]) => column.some((code) => codes.has(code));
  return { left: bound(LEFT_COLUMN), right: bound(RIGHT_COLUMN) };
}

/** Every code a Profile's layers place a Binding on (base plus every modifier layer). */
export function layerCodes(layers: Layers): Set<string> {
  const codes = new Set<string>();
  for (const layer of [layers.base, ...Object.values(layers.mods ?? {})]) {
    for (const code of Object.keys(layer)) codes.add(code);
  }
  return codes;
}
